// Deterministic ordering (IMPLEMENTATION §0.1): every `.sort(` in sim/ goes through byId, so ties never
// depend on engine sort stability (JS vs C# List.Sort).
// Comparators return a SIGN (-1/0/1), never a difference: a literal C# port returns `int` from
// Comparison<T>, and a `long` difference (cash, prices, lifetime > 2^31) would overflow there.
import type { Int } from './types.ts';

export interface HasId {
  id: Int;
}

/** -1 / 0 / 1 without subtraction (port-safe for long keys). */
export function cmpInt(a: Int, b: Int): Int {
  return a < b ? -1 : a > b ? 1 : 0;
}

/** Compares by `key` first (optional), then by id — a total order for unique ids. */
export function byId<T extends HasId>(key?: (v: T) => Int): (a: T, b: T) => Int {
  return (a, b) => {
    if (key) {
      const c = cmpInt(key(a), key(b));
      if (c !== 0) return c;
    }
    return cmpInt(a.id, b.id);
  };
}

/** Sorted copy; never mutates the input. */
export function sortedById<T extends HasId>(xs: readonly T[], key?: (v: T) => Int): T[] {
  return xs.slice().sort(byId(key));
}

/** Binary search in an array sorted by ascending id: the index of `id`, or -1. */
export function indexById<T extends HasId>(xs: readonly T[], id: Int): Int {
  let lo = 0;
  let hi = xs.length - 1;
  while (lo <= hi) {
    const mid = (lo + hi) >>> 1;
    const v = (xs[mid] as T).id;
    if (v === id) return mid;
    if (v < id) lo = mid + 1;
    else hi = mid - 1;
  }
  return -1;
}

/** Inserts `v` keeping ascending id order (ids are unique). */
export function insertById<T extends HasId>(xs: T[], v: T): void {
  let i = xs.length;
  while (i > 0 && (xs[i - 1] as T).id > v.id) i--;
  xs.splice(i, 0, v);
}
